/**
 * Magic bytes (file signatures) for image format detection
 *
 * Magic bytes are unique byte sequences at the start of files
 * that identify the file format. This is more reliable than
 * file extensions or MIME types for binary data from S3.
 *
 * References:
 * - https://en.wikipedia.org/wiki/List_of_file_signatures
 * - https://www.garykessler.net/library/file_sigs.html
 */

/**
 * Image format signature definition
 */
export interface ImageMagicBytesSignature {
  /** Human-readable format name */
  format: string;
  /** MIME type for the format */
  mimeType: string;
  /** Byte signature as hex array */
  signature: number[];
  /** Byte offset where signature starts (usually 0) */
  offset: number;
  /** Additional validation function (optional) */
  validate?: (bytes: Uint8Array) => boolean;
  /** Description of the format */
  description: string;
}

/**
 * Image format signatures ordered by prevalence
 * (most common formats first for faster detection)
 */
export const IMAGE_MAGIC_BYTES: ImageMagicBytesSignature[] = [
  {
    format: 'PNG',
    mimeType: 'image/png',
    signature: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a],
    offset: 0,
    description:
      'Portable Network Graphics - 8-byte signature starting with \\x89PNG',
  },
  {
    format: 'JPEG',
    mimeType: 'image/jpeg',
    signature: [0xff, 0xd8, 0xff],
    offset: 0,
    validate: (_bytes: Uint8Array) => {
      // JPEG must end with FF D9 (EOI marker)
      // Only validate if we have full file (not just first bytes)
      return true; // Skip validation for streaming detection
    },
    description: 'Joint Photographic Experts Group - starts with FF D8 FF',
  },
  {
    format: 'GIF',
    mimeType: 'image/gif',
    signature: [0x47, 0x49, 0x46, 0x38],
    offset: 0,
    validate: (bytes: Uint8Array) => {
      // GIF87a or GIF89a
      if (bytes.length < 6) return false;
      return (
        (bytes[4] === 0x37 || bytes[4] === 0x39) && // 7 or 9
        bytes[5] === 0x61 // 'a'
      );
    },
    description: 'Graphics Interchange Format - starts with GIF8 (87a or 89a)',
  },
  {
    format: 'WebP',
    mimeType: 'image/webp',
    signature: [0x52, 0x49, 0x46, 0x46], // RIFF
    offset: 0,
    validate: (bytes: Uint8Array) => {
      // WEBP signature at offset 8
      if (bytes.length < 12) return false;
      return (
        bytes[8] === 0x57 && // W
        bytes[9] === 0x45 && // E
        bytes[10] === 0x42 && // B
        bytes[11] === 0x50 // P
      );
    },
    description: 'WebP format - RIFF container with WEBP signature at offset 8',
  },
  {
    format: 'BMP',
    mimeType: 'image/bmp',
    signature: [0x42, 0x4d], // BM
    offset: 0,
    description: 'Windows Bitmap - starts with BM',
  },
  {
    format: 'TIFF (Little Endian)',
    mimeType: 'image/tiff',
    signature: [0x49, 0x49, 0x2a, 0x00], // II*\0
    offset: 0,
    description: 'Tagged Image File Format (Intel byte order)',
  },
  {
    format: 'TIFF (Big Endian)',
    mimeType: 'image/tiff',
    signature: [0x4d, 0x4d, 0x00, 0x2a], // MM\0*
    offset: 0,
    description: 'Tagged Image File Format (Motorola byte order)',
  },
  {
    format: 'ICO',
    mimeType: 'image/x-icon',
    signature: [0x00, 0x00, 0x01, 0x00],
    offset: 0,
    description: 'Windows Icon format',
  },
];

/**
 * Minimum bytes needed for detection
 */
export const MIN_BYTES_FOR_DETECTION = Math.max(
  ...IMAGE_MAGIC_BYTES.map(sig => sig.offset + sig.signature.length)
);

/**
 * Performance thresholds for image detection strategy
 */
export const IMAGE_DETECTION_CONFIG = {
  /** Maximum file size (5MB) before skipping magic byte detection */
  MAX_SIZE_FOR_MAGIC_BYTES: 5 * 1024 * 1024,
  /** Number of bytes to read for magic byte detection */
  MAGIC_BYTE_READ_LENGTH: 16,
  /** Cache TTL for detected image types (5 minutes) */
  CACHE_TTL_MS: 5 * 60 * 1000,
  /** Fetch timeout for file download (10 seconds) */
  FETCH_TIMEOUT_MS: 10 * 1000,
  /** Display timeout for large files in UI (3 seconds) - shows "too large" message */
  DISPLAY_TIMEOUT_MS: 3 * 1000,
  /** Maximum file size (10MB) for binary detection - larger files assumed binary to prevent memory crashes */
  MAX_SIZE_FOR_BINARY_DETECTION: 10 * 1024 * 1024,
} as const;

/**
 * File extension to MIME type mapping
 */
export const EXTENSION_MIME_TYPES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  bmp: 'image/bmp',
  tiff: 'image/tiff',
  tif: 'image/tiff',
  ico: 'image/x-icon',
};
